import { ThemeProvider } from 'next-themes'
import dynamic from 'next/dynamic'
import React, { useEffect, useMemo, useState } from 'react'
import Head from 'next/head'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { GatewayProvider } from '@components/Gateway/GatewayProvider'
import { VSR_PLUGIN_PKS } from '@constants/plugins'
import ErrorBoundary from '@components/ErrorBoundary'
import useHandleGovernanceAssetsStore from '@hooks/handleGovernanceAssetsStore'
import handleRouterHistory from '@hooks/handleRouterHistory'
import NavBar from '@components/NavBar'
import PageBodyContainer from '@components/PageBodyContainer'
import tokenPriceService from '@utils/services/tokenPrice'
import TransactionLoader from '@components/TransactionLoader'
import useDepositStore from 'plugins/VoteStakeRegistry/stores/useDepositStore'
import useRealm from '@hooks/useRealm'
import NftVotingCountingModal from '@components/NftVotingCountingModal'
import { getResourcePathPart } from '@tools/core/resources'
import useSerumGovStore from 'stores/useSerumGovStore'
import useWalletOnePointOh from '@hooks/useWalletOnePointOh'
import { useUserCommunityTokenOwnerRecord } from '@hooks/queries/tokenOwnerRecord'
import { useRealmQuery } from '@hooks/queries/realm'
import { useRealmConfigQuery } from '@hooks/queries/realmConfig'
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react'
import useLegacyConnectionContext from '@hooks/useLegacyConnectionContext'
import { DEVNET_RPC, MAINNET_RPC } from 'constants/endpoints'
import {
  SquadsEmbeddedWalletAdapter,
  detectEmbeddedInSquadsIframe,
} from '@sqds/iframe-adapter'
import { WALLET_PROVIDERS } from '@utils/wallet-adapters'
import { tryParsePublicKey } from '@tools/core/pubkey'
import { useAsync } from 'react-async-hook'
import { useVsrClient } from '../plugins/VoterWeightPlugins/useVsrClient'
import { useRealmVoterWeightPlugins } from '@hooks/useRealmVoterWeightPlugins'
import { Sidebar } from './Sidebar'
import cx from '@hub/lib/cx'
import { useTheme } from 'next-themes'
import Modal from '@components/Modal'

const ConnectWalletButtonDynamic = dynamic(
  async () => await import('./ConnectWalletButton'),
  { ssr: false },
)
const Notifications = dynamic(() => import('../components/Notification'), {
  ssr: false,
})

const GoogleTag = React.memo(
  function GoogleTag() {
    return (
      <React.Fragment>
        <Script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-TG90SK6TGB"
        />
        <Script id="gta-main">{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-TG90SK6TGB');
        `}</Script>
      </React.Fragment>
    )
  },
  () => true,
)

interface Props {
  children: React.ReactNode
}

/** AppContents depends on providers itself, sadly, so this is where providers go.  */
export function App(props: Props) {
  const { setTheme } = useTheme()
  const CURRENT_CHAIN = process.env.CURRENT_CHAIN || 'devnet'
  const endpoint = CURRENT_CHAIN === 'devnet' ? DEVNET_RPC : MAINNET_RPC
  const [isWalletConnected, setIsWalletConnected] = useState(false)
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return
    const storedIsWalletConnected = localStorage.getItem('isWalletConnected')
    const isWalletConnected = JSON.parse(storedIsWalletConnected || 'false')

    setIsConnectModalOpen(!isWalletConnected)
    setIsWalletConnected(isWalletConnected)
  }, [])

  // TODO: Remove or config light theme
  useEffect(() => {
    setTheme('Dark')
  }, [])

  const supportedWallets = useMemo(
    () =>
      detectEmbeddedInSquadsIframe()
        ? [new SquadsEmbeddedWalletAdapter()]
        : WALLET_PROVIDERS.map((provider) => provider.adapter),
    [],
  )

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={supportedWallets} autoConnect={isWalletConnected}>
      {isConnectModalOpen && 
          <Modal
            isOpen={isConnectModalOpen}
            onClose={() => setIsConnectModalOpen(false)}
          >
            <div className="p-4">
              <h1 className="text-center mb-8">Connect wallet</h1>
              <div className="flex flex-col gap-4 justify-center items-center">
                <span className="text-center">
                  To ensure the best user experience, please connect your wallet.
                </span>
                <ConnectWalletButtonDynamic onSuccessConnection={() => setIsConnectModalOpen(false)} />
              </div>
            </div>
          </Modal>}
        <AppContents {...props} />
      </WalletProvider>
    </ConnectionProvider>
  )
}

const allowedFaviconPaths = ['/realms/']
const allowedDomains = [
  'https://app.realms.today',
  'http://localhost',
  'http://localhost:3000',
]

export function AppContents(props: Props) {
  handleRouterHistory()
  useHandleGovernanceAssetsStore()
  useEffect(() => {
    tokenPriceService.fetchSolanaTokenListV2()
  }, [])
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const { getOwnedDeposits, resetDepositState } = useDepositStore()

  const { plugins } = useRealmVoterWeightPlugins('community')
  const usesVsr = plugins?.voterWeight.find((plugin) =>
    VSR_PLUGIN_PKS.includes(plugin.programId.toString()),
  )
  const ownTokenRecord = useUserCommunityTokenOwnerRecord().data?.result

  const realm = useRealmQuery().data?.result
  const config = useRealmConfigQuery().data?.result

  const { realmInfo } = useRealm()
  const wallet = useWalletOnePointOh()
  const connection = useLegacyConnectionContext()

  const router = useRouter()
  const { cluster } = router.query
  const updateSerumGovAccounts = useSerumGovStore(
    (s) => s.actions.updateSerumGovAccounts,
  )
  const { vsrClient } = useVsrClient()

  const realmName = realmInfo?.displayName ?? realm?.account?.name
  const title = realmName ? `${realmName}` : 'Q DAO'
  const REALM = process.env.REALM

  // Note: ?v==${Date.now()} is added to the url to force favicon refresh.
  // Without it browsers would cache the last used and won't change it for different realms
  // https://stackoverflow.com/questions/2208933/how-do-i-force-a-favicon-refresh

  const faviconUrl = useMemo(() => {
    const symbol = REALM

    if (!symbol || tryParsePublicKey(symbol as string) !== undefined) {
      return null
    }
    if (!isValidSymbol(symbol)) {
      console.error('Invalid symbol')
      return null
    }

    const resourcePath = getResourcePathPart(symbol as string)
    const fullUrl = `${
      window.location.origin
    }/realms/${resourcePath}/favicon.ico?v=${Date.now()}`

    // Check if the domain is in the allow list
    try {
      const urlObject = new URL(fullUrl)
      if (!allowedDomains.includes(urlObject.origin)) {
        console.error('Domain not in allowed list')
        return null
      }
      // Check if the path is in the allow list
      if (
        !allowedFaviconPaths.some((path) => urlObject.pathname.startsWith(path))
      ) {
        console.error('Path not in allowed list')
        return null
      }

      return urlObject.href
    } catch (error) {
      console.error('Invalid URL:', error)
      return null
    }
  }, [REALM])

  // Validate it's an ico file
  function isValidSymbol(symbol) {
    return (
      typeof symbol === 'string' &&
      symbol.trim() !== '' &&
      /^[a-zA-Z0-9-_]+$/.test(symbol)
    )
  }
  const { result: faviconExists } = useAsync(async () => {
    if (!faviconUrl) {
      return false
    }

    try {
      const response = await fetch(faviconUrl)
      return response.status === 200
    } catch (error) {
      console.error('Error fetching favicon:', error)
      return false
    }
  }, [faviconUrl])

  useEffect(() => {
    if (
      realm &&
      usesVsr &&
      realm.pubkey &&
      wallet?.connected &&
      ownTokenRecord &&
      vsrClient
    ) {
      getOwnedDeposits({
        realmPk: realm.pubkey,
        communityMintPk: realm.account.communityMint,
        walletPk: ownTokenRecord!.account!.governingTokenOwner,
        client: vsrClient,
        connection: connection.current,
      })
    } else if (!wallet?.connected || !ownTokenRecord) {
      resetDepositState()
    }
  }, [
    config?.account.communityTokenConfig.voterWeightAddin,
    connection,
    getOwnedDeposits,
    ownTokenRecord,
    realm,
    resetDepositState,
    vsrClient,
    wallet?.connected,
  ])

  useEffect(() => {
    updateSerumGovAccounts(cluster as string | undefined)
  }, [cluster, updateSerumGovAccounts])

  return (
    <div className="relative bg-bkg-1 text-fgd-1">
      <Head>
        <meta property="og:title" content={title} key="title" />
        <title>{title}</title>
        <style>{`
          body {
            background-color: #17161c;
          }
        `}</style>
        {faviconUrl && faviconExists ? (
          <>
            <link rel="icon" href={faviconUrl} />
          </>
        ) : (
          <>
            <link
              rel="icon"
              type="image/png"
              sizes="192x192"
              href="/favicon.ico"
            />
          </>
        )}
      </Head>
      <GoogleTag />
      <ErrorBoundary>
        <ThemeProvider defaultTheme="Dark">
          <GatewayProvider>
            <NavBar
              onBurgerClick={() => {
                setSidebarOpen(true)
              }}
            />
            <Notifications />
            <TransactionLoader></TransactionLoader>
            <NftVotingCountingModal />
            <div
              className={cx(
                'xl:grid-cols-[200px,1fr]',
                'grid',
                'overflow-x-visible',
              )}
            >
              <Sidebar
                isOpen={isSidebarOpen}
                onClose={() => setSidebarOpen(false)}
              />
              <PageBodyContainer>
                {props.children}
              </PageBodyContainer>
            </div>
          </GatewayProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  )
}
